import { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import * as API from "../../networking/policies";
import { RiskSettings } from "../reducers/types/commonTypes";
import { RootState } from "../store";
import { getRisksForPolicy } from "./risk";
import {
    CREATE_POLICY_FAILURE,
    CREATE_POLICY_PENDING,
    CREATE_POLICY_SUCCESS,
    GET_AVAILABLE_POLICIES_FAILURE,
    GET_AVAILABLE_POLICIES_PENDING,
    GET_AVAILABLE_POLICIES_SUCCESS,
    GET_POLICY_RISK_SETTINGS_FAILURE,
    GET_POLICY_RISK_SETTINGS_PENDING,
    GET_POLICY_RISK_SETTINGS_SUCCESS,
    GET_USER_POLICIES_FAILURE,
    GET_USER_POLICIES_PENDING,
    GET_USER_POLICIES_SUCCESS,
    JOIN_POLICY_FAILURE,
    JOIN_POLICY_PENDING,
    JOIN_POLICY_SUCCESS,
    PATCH_POLICY_FAILURE,
    PATCH_POLICY_PENDING,
    PATCH_POLICY_RISK_SETTINGS_FAILURE,
    PATCH_POLICY_RISK_SETTINGS_PENDING,
    PATCH_POLICY_RISK_SETTINGS_SUCCESS,
    PATCH_POLICY_SUCCESS,
} from "./types";

export const getAvailablePolicies =
    (
        page: number,
        perPage: number,
        hasQuote?: boolean
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        dispatch({ type: GET_AVAILABLE_POLICIES_PENDING });
        try {
            const response = await API.getAvailablePolicies({ 
                page, 
                perPage,
                ...(hasQuote !== undefined && { has_quote: hasQuote.toString() })
            });
            dispatch({
                type: GET_AVAILABLE_POLICIES_SUCCESS,
                payload: response.data,
            });
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                dispatch({
                    type: GET_AVAILABLE_POLICIES_FAILURE,
                    payload: error.message,
                });
            }
        }
    };

export const getUserPolicies =
    (): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        dispatch({ type: GET_USER_POLICIES_PENDING });
        try {
            const response = await API.getUserPolicies();
            dispatch({
                type: GET_USER_POLICIES_SUCCESS,
                payload: response.data,
            });
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                dispatch({
                    type: GET_USER_POLICIES_FAILURE,
                    payload: error.message,
                });
            }
        }
    };

export const createPolicy =
    (
        values: API.PolicyCreationPayload
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        dispatch({ type: CREATE_POLICY_PENDING });
        try {
            const response = await API.createPolicy(values);
            dispatch({ type: CREATE_POLICY_SUCCESS, payload: response.data });
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                const errorData = error.response?.data || {};
                dispatch({ 
                    type: CREATE_POLICY_FAILURE, 
                    payload: {
                        message: errorData.message || errorData.detail || "Unable to create policy. Please check your input and try again.",
                        errors: errorData.errors || errorData,
                        status: error.response?.status,
                    }
                });
            } else {
                dispatch({ 
                    type: CREATE_POLICY_FAILURE, 
                    payload: {
                        message: "An unexpected error occurred. Please try again.",
                        errors: {},
                    }
                });
            }
        }
    };

export const patchPolicy =
    (
        policyId: number,
        values: API.PolicyPatchPayload
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        dispatch({ type: PATCH_POLICY_PENDING });
        try {
            const response = await API.patchPolicy(policyId, values);
            dispatch({ type: PATCH_POLICY_SUCCESS, payload: response.data });
        } catch (error) {
            dispatch({ type: PATCH_POLICY_FAILURE, payload: error });
        }
    };

export const joinPolicy =
    (
        policyId: number,
        onSuccess: () => void
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        dispatch({ type: JOIN_POLICY_PENDING });
        try {
            const response = await API.joinPolicy(policyId);
            dispatch({ type: JOIN_POLICY_SUCCESS, payload: response.data });
            onSuccess();
        } catch (error) {
            dispatch({ type: JOIN_POLICY_FAILURE, payload: error });
        }
    };

export const getPolicyRiskSettings =
    (policyId: number): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        dispatch({ type: GET_POLICY_RISK_SETTINGS_PENDING });
        try {
            const response = await API.getPolicyRiskSettings(policyId);
            dispatch({
                type: GET_POLICY_RISK_SETTINGS_SUCCESS,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: GET_POLICY_RISK_SETTINGS_FAILURE,
                payload: {
                    error,
                    policy: policyId,
                },
            });
        }
    };

export const updatePolicyRiskSettings =
    (
        policyId: number,
        values: Partial<RiskSettings>,
        onSuccess: () => void,
        onError: (err: any) => void = () => {} // can be omitted
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        dispatch({ type: PATCH_POLICY_RISK_SETTINGS_PENDING });
        try {
            const response = await API.updatePolicyRiskSettings(
                policyId,
                values
            );
            dispatch({
                type: PATCH_POLICY_RISK_SETTINGS_SUCCESS,
                payload: response.data,
            });
            dispatch(getRisksForPolicy(policyId));
            onSuccess();
        } catch (error) {
            dispatch({
                type: PATCH_POLICY_RISK_SETTINGS_FAILURE,
                payload: {
                    error,
                    policy: policyId,
                },
            });
            onError(error);
        }
    };
